import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * Chat-specific WebSocket hook with conversation-based state management.
 * @param {string} conversationId - Optional conversation ID
 * @returns {Object} Chat WebSocket state and methods
 */
export function useChatWebSocket(conversationId) {
  const navigate = useNavigate();
  const ws = useWebSocket();

  // Conversations keyed by ID: { [id]: { id, messages, product, isLoading, isTyping } }
  const [conversations, setConversations] = useState({});
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);

  // Sync currentConversationId with prop when navigating to different conversation
  useEffect(() => {
    if (conversationId && conversationId !== currentConversationId) {
      setCurrentConversationId(conversationId);
    }
  }, [conversationId, currentConversationId]);

  // Update conversation (supports object or function updates)
  const updateConversation = useCallback((convId, updates) => {
    if (!convId) return;
    setConversations(prev => {
      const currentConv = prev[convId] || { id: convId, messages: [], product: null, isLoading: false, isTyping: false };
      const newData = typeof updates === 'function' ? updates(currentConv) : updates;
      return {
        ...prev,
        [convId]: {
          ...currentConv,
          ...newData
        }
      };
    });
  }, []);

  // Delete conversation from cache
  const deleteConversation = useCallback((convId) => {
    setConversations(prev => {
      const next = { ...prev };
      delete next[convId];
      return next;
    });
  }, []);

  // Current conversation or default empty state
  const currentConversation = conversations[currentConversationId] || {
    id: null,
    messages: [],
    product: null,
    isLoading: false,
    isTyping: false
  };

  // Subscribe to chat channel and load history when conversation changes
  useEffect(() => {
    if (!ws.isConnected || !conversationId) return;

    const channel = `chat:${conversationId}`;
    ws.subscribe(channel);
    updateConversation(conversationId, { isLoading: true });
    ws.send({ type: 'load_history', conversation_id: conversationId });

    return () => ws.unsubscribe(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, ws.isConnected]);

  // Register message handlers
  useEffect(() => {
    const handleMessage = (data) => {
      if (!currentConversationId) return;

      updateConversation(currentConversationId, prev => ({
        messages: [
          ...prev.messages,
          {
            role: data.role,
            content: data.content,
            timestamp: data.timestamp || new Date().toISOString(),
            metadata: data.metadata || null,
          }
        ],
        isTyping: false
      }));
    };

    const handleConversationUpdated = (data) => {
      if (!data.conversation_id || data.conversation_id === currentConversationId) return;

      setCurrentConversationId(data.conversation_id);
      window.dispatchEvent(new CustomEvent('conversationUpdated', {
        detail: { conversationId: data.conversation_id, isNew: data.is_new }
      }));

      if (data.is_new && !conversationId) {
        navigate(`/home/${data.conversation_id}`, { replace: true });
      }

      ws.subscribe(`chat:${data.conversation_id}`);
    };

    const handleTyping = (data) => {
      if (!currentConversationId) return;
      updateConversation(currentConversationId, { isTyping: data.isTyping });
    };

    const handleHistory = (data) => {
      if (!conversationId) return;

      updateConversation(conversationId, prev => {
        // If offset > 0, this is loading older messages (prepend)
        // If offset = 0, this is initial load (replace)
        const messages = data.offset > 0
          ? [...(Array.isArray(data.messages) ? data.messages : []), ...prev.messages]
          : Array.isArray(data.messages) ? data.messages : [];

        return {
          messages,
          product: (data.product_id && data.product_name) ? { id: data.product_id, name: data.product_name } : null,
          isLoading: false,
          hasMore: data.has_more || false,
          totalMessages: data.total || messages.length
        };
      });
    };

    const handleProductContext = (data) => {
      if (!currentConversationId || !data.product) return;
      updateConversation(currentConversationId, { product: data.product });
    };

    const handleTitleUpdated = (data) => {
      window.dispatchEvent(new CustomEvent('conversationUpdated', {
        detail: { conversationId: data.conversation_id, isNew: false }
      }));
    };

    const handleError = (data) => {
      console.log('[useChatWebSocket] Error received:', data);

      if (currentConversationId) {
        updateConversation(currentConversationId, { isTyping: false });
      }

      // Note: Error handling is now done via NotificationContext
      // The 'error' type messages are processed by the built-in WebSocket handler
      // "no_products" error is now handled in response_complete (not here)
    };

    const handleConversationNotFound = (data) => {
      if (data.conversation_id) {
        deleteConversation(data.conversation_id);
      }
      navigate('/home', { replace: true });
    };

    const handleStreamChunk = (data) => {
      const targetConvId = currentConversationId || 'temp';
      if (!targetConvId) return;

      updateConversation(targetConvId, prev => {
        const messages = [...prev.messages];
        const lastMessage = messages[messages.length - 1];

        // If last message is streaming, append chunk
        if (lastMessage && lastMessage.isStreaming) {
          lastMessage.content += data.content;
        } else {
          // Start new streaming message
          messages.push({
            role: 'assistant',
            content: data.content,
            timestamp: new Date().toISOString(),
            metadata: null,
            isStreaming: true
          });
        }

        return { ...prev, messages, isTyping: false };
      });
    };

    const handleStreamComplete = (data) => {
      if (!data.conversation_id) return;

      // Handle new conversation
      if (data.is_new && !conversationId) {
        const tempConv = conversations['temp'] || { messages: [] };

        setConversations(prev => {
          const next = { ...prev };
          delete next['temp'];

          // Find the streaming message and finalize it
          const messages = tempConv.messages.map(msg => {
            if (msg.isStreaming) {
              return {
                ...msg,
                isStreaming: false,
                timestamp: data.timestamp,
                metadata: data.metadata
              };
            }
            return msg;
          });

          next[data.conversation_id] = {
            id: data.conversation_id,
            messages,
            product: data.product || null,
            isLoading: false,
            isTyping: false
          };

          return next;
        });

        setCurrentConversationId(data.conversation_id);
        navigate(`/home/${data.conversation_id}`, { replace: true });
        window.dispatchEvent(new CustomEvent('conversationUpdated', {
          detail: { conversationId: data.conversation_id, isNew: true }
        }));
        ws.subscribe(`chat:${data.conversation_id}`);
      } else {
        // Update existing conversation - finalize streaming message
        updateConversation(data.conversation_id, prev => {
          const messages = prev.messages.map(msg => {
            if (msg.isStreaming) {
              return {
                ...msg,
                isStreaming: false,
                timestamp: data.timestamp,
                metadata: data.metadata
              };
            }
            return msg;
          });

          return {
            ...prev,
            messages,
            product: data.product || prev.product,
            isTyping: false
          };
        });

        window.dispatchEvent(new CustomEvent('conversationUpdated', {
          detail: { conversationId: data.conversation_id, isNew: false }
        }));
      }
    };

    const handleResponseComplete = (data) => {
      if (!data.conversation_id) return;

      // Check for "no products" error - trigger Create Product modal
      if (data.error_code === 'no_products') {
        window.dispatchEvent(new CustomEvent('noProductsError', {
          detail: { message: data.message?.content || 'No products available' }
        }));
        // Continue to display the message normally below
      }

      // Handle new conversation
      if (data.is_new && !conversationId) {
        // Get messages from temp conversation (includes user's message)
        const tempConv = conversations['temp'] || { messages: [] };

        // Create new conversation with real ID, transferring temp messages
        setConversations(prev => {
          const next = { ...prev };
          delete next['temp']; // Remove temp conversation

          next[data.conversation_id] = {
            id: data.conversation_id,
            messages: [
              ...tempConv.messages, // User's message from temp
              {
                role: data.message.role,
                content: data.message.content,
                timestamp: data.message.timestamp,
                metadata: data.message.metadata
              }
            ],
            product: data.product || null,
            isLoading: false,
            isTyping: false
          };

          return next;
        });

        setCurrentConversationId(data.conversation_id);
        navigate(`/home/${data.conversation_id}`, { replace: true });
        window.dispatchEvent(new CustomEvent('conversationUpdated', {
          detail: { conversationId: data.conversation_id, isNew: true }
        }));
        ws.subscribe(`chat:${data.conversation_id}`);
      } else {
        // Update existing conversation
        updateConversation(data.conversation_id, prev => ({
          messages: [
            ...prev.messages,
            {
              role: data.message.role,
              content: data.message.content,
              timestamp: data.message.timestamp,
              metadata: data.message.metadata
            }
          ],
          product: data.product || prev.product,
          isTyping: false
        }));

        // Trigger conversation list refresh
        window.dispatchEvent(new CustomEvent('conversationUpdated', {
          detail: { conversationId: data.conversation_id, isNew: false }
        }));
      }
    };

    const handleFormSubmitted = (data) => {
      if (!data.conversation_id || data.message_index === undefined) return;

      // Update the specific message's metadata to mark form as submitted
      updateConversation(data.conversation_id, prev => {
        const messages = [...prev.messages];
        if (messages[data.message_index]) {
          messages[data.message_index] = {
            ...messages[data.message_index],
            metadata: {
              ...messages[data.message_index].metadata,
              form_submitted: true
            }
          };
        }
        return { ...prev, messages };
      });
    };

    // Register all handlers
    const cleanupFns = [
      ws.on('message', handleMessage),
      ws.on('conversation_updated', handleConversationUpdated),
      ws.on('typing', handleTyping),
      ws.on('history', handleHistory),
      ws.on('product_context', handleProductContext),
      ws.on('title_updated', handleTitleUpdated),
      ws.on('conversation_not_found', handleConversationNotFound),
      ws.on('stream_chunk', handleStreamChunk),
      ws.on('stream_complete', handleStreamComplete),
      ws.on('response_complete', handleResponseComplete),
      ws.on('form_submitted', handleFormSubmitted),
      ws.on('error', handleError),
    ];

    return () => {
      // Cleanup all handlers
      cleanupFns.forEach(cleanup => cleanup());
    };
    // ws.on/ws.subscribe use refs internally and are safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, conversationId, navigate, updateConversation, deleteConversation]);

  const sendMessage = useCallback((content) => {
    if (!ws.isConnected) {
      console.error('[Chat] Cannot send - not connected');
      return false;
    }

    const targetConvId = currentConversationId || 'temp';

    // For new conversations, set current to 'temp' so UI displays the message
    if (!currentConversationId) {
      setCurrentConversationId('temp');
    }

    // Add user message to local state immediately (optimistic update)
    // Use 'temp' ID for new conversations until we get the real ID back
    updateConversation(targetConvId, prev => ({
      messages: [
        ...prev.messages,
        {
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
          metadata: null,
        }
      ],
      isTyping: true
    }));

    // Send to server
    return ws.send({
      type: 'message',
      content,
      conversation_id: currentConversationId || null
    });
  }, [currentConversationId, ws.isConnected, ws.send, updateConversation, setCurrentConversationId]);

  const loadMoreMessages = useCallback(() => {
    if (!conversationId || !ws.isConnected) return false;

    const conv = conversations[conversationId];
    if (!conv || !conv.hasMore || conv.isLoading) return false;

    // Request next page of older messages
    const currentOffset = conv.messages.length;
    updateConversation(conversationId, { isLoading: true });

    return ws.send({
      type: 'load_history',
      conversation_id: conversationId,
      offset: currentOffset,
      limit: 50
    });
  }, [conversationId, conversations, ws.isConnected, ws.send, updateConversation]);

  return {
    currentConversation,
    sendMessage,
    loadMoreMessages,
    deleteConversation,
    isConnected: ws.isConnected,
    isInitialConnection: ws.isInitialConnection,
    error: ws.error,
    currentConversationId,
    conversations,
    reconnect: ws.connect,  // Expose connect as reconnect for clarity
  };
}
